// Invoice service — handles creation, numbering, tax calculation, and storage (mock)

import type {
  Invoice,
  InvoiceConfig,
  InvoiceCustomer,
  InvoiceItem,
  InvoiceNumberSequence,
  OrderData,
} from "./types";
import { InvoiceStatus } from "./types";

const STORAGE_KEY_SEQUENCE = "djayma-invoice-sequence";
const STORAGE_KEY_INVOICES = "djayma-invoices";

// ---------------------------------------------------------------------------
// Company details
// ---------------------------------------------------------------------------

export function getCompanyDetails(): InvoiceConfig {
  return {
    companyName: "CIEDA SARL",
    companyAddress: "Hann Mariste II, Dakar, Sénégal",
    companyPhone: "+221 76 604 22 74",
    companyEmail: "contact@cieda.sn",
    ninea: "XXXXXXXXX", // placeholder — will be configured
    rcNumber: "SN-DKR-XXXX-XXXX", // placeholder — will be configured
    defaultTaxRate: 0.18,
    invoicePrefix: "DJ",
    currency: "XOF",
  };
}

// ---------------------------------------------------------------------------
// Invoice number formatting & generation
// ---------------------------------------------------------------------------

export function formatInvoiceNumber(
  prefix: string,
  year: number,
  sequence: number,
): string {
  const padded = String(sequence).padStart(5, "0");
  return `${prefix}-${year}-${padded}`;
}

function loadSequence(year: number): InvoiceNumberSequence {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_SEQUENCE);
    if (raw) {
      const stored: InvoiceNumberSequence = JSON.parse(raw);
      if (stored.year === year) {
        return stored;
      }
    }
  } catch {
    // corrupted storage — reset
  }
  return { year, lastNumber: 0, prefix: "DJ" };
}

function saveSequence(seq: InvoiceNumberSequence): void {
  localStorage.setItem(STORAGE_KEY_SEQUENCE, JSON.stringify(seq));
}

export function generateInvoiceNumber(year: number): string {
  const current = loadSequence(year);
  const nextNumber = current.lastNumber + 1;
  const updated: InvoiceNumberSequence = {
    ...current,
    lastNumber: nextNumber,
  };
  saveSequence(updated);
  return formatInvoiceNumber(updated.prefix, year, nextNumber);
}

// ---------------------------------------------------------------------------
// Tax calculation
// ---------------------------------------------------------------------------

export function calculateTax(
  subtotal: number,
  rate: number,
): { taxAmount: number; total: number } {
  const taxAmount = Math.round(subtotal * rate);
  return { taxAmount, total: subtotal + taxAmount };
}

// ---------------------------------------------------------------------------
// Invoice creation
// ---------------------------------------------------------------------------

function generateId(): string {
  return `inv_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export function createInvoice(
  order: OrderData,
  customer: InvoiceCustomer,
): Invoice {
  const config = getCompanyDetails();
  const now = new Date();
  const year = now.getFullYear();

  const items: readonly InvoiceItem[] = order.items.map((item) => ({
    description: item.description,
    quantity: item.quantity,
    unitPrice: item.unitPriceHt,
    totalPrice: item.unitPriceHt * item.quantity,
    taxRate: item.tvaRate,
  }));

  const subtotal = items.reduce((sum, item) => sum + item.totalPrice, 0);
  const { taxAmount, total } = calculateTax(subtotal, config.defaultTaxRate);

  const invoice: Invoice = {
    id: generateId(),
    number: generateInvoiceNumber(year),
    date: now.toISOString(),
    dueDate: now.toISOString(), // payment at order — no deferred due date
    customer,
    items,
    subtotal,
    taxRate: config.defaultTaxRate,
    taxAmount,
    total,
    currency: "XOF",
    status: InvoiceStatus.ISSUED,
    notes: order.notes,
  };

  persistInvoice(invoice);
  return invoice;
}

// ---------------------------------------------------------------------------
// Mock persistence (localStorage)
// ---------------------------------------------------------------------------

function loadInvoices(): readonly Invoice[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_INVOICES);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function persistInvoice(invoice: Invoice): void {
  const existing = loadInvoices();
  const updated = [...existing, invoice];
  localStorage.setItem(STORAGE_KEY_INVOICES, JSON.stringify(updated));
}

export function getInvoiceById(invoiceId: string): Invoice | undefined {
  return loadInvoices().find((inv) => inv.id === invoiceId);
}

export function getInvoicesByCustomer(customerId: string): readonly Invoice[] {
  // In a real implementation this would query by customer_id.
  // For now, return all invoices filtered by customer name match.
  return loadInvoices().filter(
    (inv) =>
      inv.customer.phone === customerId ||
      inv.customer.email === customerId,
  );
}

export function getAllInvoices(): readonly Invoice[] {
  return loadInvoices();
}
